import type {
  CreateStationAssignmentPayload,
  StationAssignment,
  StationStaffRole,
} from './stationStaffService'

export type DesiredStationAssignments = Record<StationStaffRole, string>

export type StationAssignmentOperation =
  | { action: 'unchanged'; role: StationStaffRole; assignment: StationAssignment }
  | { action: 'create'; role: StationStaffRole; userResourceId: string }
  | { action: 'delete'; role: StationStaffRole; assignment: StationAssignment }
  | {
      action: 'replace'
      role: StationStaffRole
      assignment: StationAssignment
      userResourceId: string
    }

export type StationAssignmentOperationResult = {
  operation: StationAssignmentOperation
  succeeded: boolean
  error?: unknown
  previousAssignmentRestored?: boolean
  restoreError?: unknown
}

const ROLES: StationStaffRole[] = ['STATION_MANAGER', 'STATION_ASSOCIATE']

export const planStationAssignmentChanges = (
  existingAssignments: StationAssignment[],
  desiredAssignments: DesiredStationAssignments,
): StationAssignmentOperation[] => {
  const operations: StationAssignmentOperation[] = []

  ROLES.forEach((role) => {
    const existing = existingAssignments.find((assignment) => assignment.role === role)
    const desiredUserResourceId = desiredAssignments[role]

    if (existing && existing.userResourceId === desiredUserResourceId) {
      operations.push({ action: 'unchanged', role, assignment: existing })
      return
    }

    if (existing && !desiredUserResourceId) {
      operations.push({ action: 'delete', role, assignment: existing })
      return
    }

    if (existing && desiredUserResourceId) {
      operations.push({
        action: 'replace',
        role,
        assignment: existing,
        userResourceId: desiredUserResourceId,
      })
      return
    }

    if (desiredUserResourceId) {
      operations.push({ action: 'create', role, userResourceId: desiredUserResourceId })
    }
  })

  return operations
}

type ReconcileDependencies = {
  createAssignment: (payload: CreateStationAssignmentPayload) => Promise<unknown>
  deleteAssignment: (assignmentResourceId: string) => Promise<unknown>
}

export const reconcileStationAssignments = async (
  stationId: string,
  existingAssignments: StationAssignment[],
  desiredAssignments: DesiredStationAssignments,
  dependencies: ReconcileDependencies,
): Promise<StationAssignmentOperationResult[]> => {
  const operations = planStationAssignmentChanges(existingAssignments, desiredAssignments)
  const results: StationAssignmentOperationResult[] = []
  const createAssignment = (role: StationStaffRole, userResourceId: string) =>
    dependencies.createAssignment({ stationId, role, userResourceId })

  for (const operation of operations) {
    if (operation.action === 'unchanged') {
      results.push({ operation, succeeded: true })
      continue
    }

    try {
      if (operation.action === 'delete') {
        await dependencies.deleteAssignment(operation.assignment.resourceId)
      } else if (operation.action === 'create') {
        await createAssignment(operation.role, operation.userResourceId)
      } else {
        await dependencies.deleteAssignment(operation.assignment.resourceId)
        try {
          await createAssignment(operation.role, operation.userResourceId)
        } catch (error) {
          try {
            await createAssignment(operation.role, operation.assignment.userResourceId)
            results.push({
              operation,
              succeeded: false,
              error,
              previousAssignmentRestored: true,
            })
          } catch (restoreError) {
            results.push({
              operation,
              succeeded: false,
              error,
              previousAssignmentRestored: false,
              restoreError,
            })
          }
          continue
        }
      }

      results.push({ operation, succeeded: true })
    } catch (error) {
      results.push({ operation, succeeded: false, error })
    }
  }

  return results
}

export const describeFailedAssignmentRoles = (
  results: StationAssignmentOperationResult[],
): string =>
  results
    .filter((result) => !result.succeeded)
    .map((result) =>
      result.operation.role === 'STATION_MANAGER' ? 'station manager' : 'station associate',
    )
    .join(' and ')
